import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";

import { colors, gray } from "@/theme";

import {
  CARD_TITLE_HOVER_GRADIENT_DARK,
  CARD_TITLE_HOVER_GRADIENT_LIGHT,
  CARD_TITLE_OVERLAY_GRADIENT_DARK,
  CARD_TITLE_OVERLAY_GRADIENT_LIGHT,
} from "./data";

/** How long the card takes to answer the pointer, and the curve it moves on. */
const HOVER_MS = 240;
const HOVER_EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";
/** The mascot's lighter blue: the ring around a hovered card on the dark page. */
const SKY_BLUE = "#5BC8FF";
/** The crown's gold, for a game that is a favorite, and the night glass the star sits on. */
const FAVORITE_GOLD = "#FDC445";
const FAVORITE_GLASS = "#07142E";

export const CardWrapper = styled(Grid)(({ theme }) => ({
  minHeight: "200px",
  [theme.breakpoints.down("sm")]: {
    minHeight: "auto",
  },
  height: "100%",
  position: "relative",
  "& .game-card": {
    position: "relative",
    overflow: "hidden",
    alignItems: "flex-end",
    height: "100%",
    cursor: "pointer",
    textAlign: "center",
    textDecoration: "none",
    color: "inherit",
    borderRadius: theme.shape.borderRadius,
    "&:focus-visible": { outline: "2px solid #5BC8FF", outlineOffset: 2 },
    transition: `transform ${HOVER_MS}ms ${HOVER_EASE}, box-shadow ${HOVER_MS}ms ease`,

    // The artwork grows inside the frame instead of the card growing over its
    // neighbours, which is what scaling the whole card did.
    "& img": {
      // Opacity stays in the list: the lazy loader fades the image in with it.
      transition: `transform ${HOVER_MS * 2}ms ${HOVER_EASE}, opacity 300ms ease`,
    },

    // The glint off the mascot's sunglasses: a band of light that crosses the
    // card once when the pointer arrives.
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      width: "45%",
      pointerEvents: "none",
      background: `linear-gradient(105deg, transparent 0%, ${alpha("#ffffff", 0.32)} 50%, transparent 100%)`,
      transform: "translateX(-130%) skewX(-12deg)",
    },
  },

  "& .game-card-title": {
    position: "absolute",
    left: 0,
    top: "20%",
    width: "100%",
    height: "80%",
    display: "flex",
    alignItems: "flex-end",
    background: CARD_TITLE_OVERLAY_GRADIENT_LIGHT,
    transition: `opacity ${HOVER_MS}ms ease`,
    ...theme.applyStyles("dark", { background: CARD_TITLE_OVERLAY_GRADIENT_DARK }),

    // The blue wash a hovered card's details are read on, as its own layer
    // over the plain one. Off where nothing can hover.
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      opacity: 0,
      pointerEvents: "none",
      background: CARD_TITLE_HOVER_GRADIENT_LIGHT,
      ...theme.applyStyles("dark", { background: CARD_TITLE_HOVER_GRADIENT_DARK }),
    },
    "& > *": {
      position: "relative",
      transition: `transform ${HOVER_MS}ms ${HOVER_EASE}`,
    },
  },

  // Only where there is a pointer to hover with. There a card at rest is its
  // artwork alone, and the name and details come up when the pointer arrives.
  // A touch screen has no hover - the state sticks to the last card tapped -
  // so there the details stay on the card, as they always were.
  "@media (hover: hover)": {
    // Hover is read off the wrapper rather than the card, so the pointer moving
    // onto the favorite button - a sibling drawn over the card - keeps it lit.
    "& .game-card-title": { opacity: 0, top: "40%", height: "60%" },
    // The star rides up with the card it is pinned to.
    "&:hover .game-card-favorite": { transform: "translateY(-4px)" },
    "&:hover .game-card-favorite:hover": { transform: "translateY(-4px) scale(1.12)" },
    "&:hover .game-card-favorite:active": { transform: "translateY(-4px) scale(0.92)" },
    "& .game-card-title::before": { opacity: 1 },
    "& .game-card-title > *": { transform: "translateY(12px)" },
    "&:hover .game-card .game-card-title": { opacity: 1 },
    "&:hover .game-card .game-card-title > *": { transform: "none" },
    // Keyboard users reach the card without hovering it.
    "&:focus-within .game-card .game-card-title": { opacity: 1 },
    "&:focus-within .game-card .game-card-title > *": { transform: "none" },

    // The card lifts into the blue light the mascot is drawn in.
    "&:hover .game-card": {
      transform: "translateY(-4px)",
      boxShadow: `0 0 0 2px ${colors.prime}, 0 12px 26px ${alpha(colors.prime, 0.35)}`,
      ...theme.applyStyles("dark", {
        boxShadow: `0 0 0 2px ${SKY_BLUE}, 0 12px 30px ${alpha(colors.prime, 0.5)}`,
      }),
    },
    "&:hover .game-card img": { transform: "scale(1.07)" },
    "&:hover .game-card::after": {
      transform: "translateX(330%) skewX(-12deg)",
      transition: `transform ${HOVER_MS * 3}ms ease-out`,
    },
  },

  "@media (prefers-reduced-motion: reduce)": {
    "& .game-card, & .game-card img, & .game-card-title, & .game-card-title > *": {
      transition: "none",
    },
    "&:hover .game-card": { transform: "none" },
    "&:hover .game-card img": { transform: "none" },
    "& .game-card-title > *": { transform: "none" },
    "& .game-card::after": { display: "none" },
  },
}));

/**
 * The favorite star, pinned to the card's corner.
 *
 * A dark glass chip in both color schemes, because it sits on artwork, not on
 * the page: a chip in the page's own color was white-on-white in the light
 * scheme. An outline star means "not a favorite"; the crown's gold, filled and
 * ringed, means it is one - so the state reads without relying on the shape
 * of the icon alone. Shown at every width: it was hidden on phones, which left
 * no way to favorite a game there.
 */
export const FavoriteButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "pop",
})<{ active: boolean; pop: boolean }>(({ theme, active, pop }) => ({
  position: "absolute",
  top: 8,
  right: 8,
  zIndex: 2,
  width: 34,
  height: 34,
  padding: 0,
  fontSize: 20,
  color: active ? FAVORITE_GOLD : colors.white,
  backgroundColor: alpha(FAVORITE_GLASS, 0.62),
  backdropFilter: "blur(6px)",
  border: "1px solid",
  borderColor: active ? alpha(FAVORITE_GOLD, 0.9) : alpha("#ffffff", 0.28),
  boxShadow: active
    ? `0 0 0 3px ${alpha(FAVORITE_GOLD, 0.22)}, 0 4px 10px ${alpha("#000000", 0.35)}`
    : `0 4px 10px ${alpha("#000000", 0.35)}`,
  transition: `transform 160ms ${HOVER_EASE}, background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease`,

  "&:hover": {
    backgroundColor: alpha(FAVORITE_GLASS, 0.85),
    borderColor: active ? FAVORITE_GOLD : alpha("#ffffff", 0.6),
    transform: "scale(1.12)",
  },
  "&:active": { transform: "scale(0.92)" },
  "&:focus-visible": { outline: `2px solid ${SKY_BLUE}`, outlineOffset: 2 },

  // The star pops when the player makes the game a favorite - not on every
  // favorite already on the page when it loads.
  ...(pop && {
    "& svg": {
      animation: "game-card-favorite-pop 320ms cubic-bezier(0.2, 1.6, 0.4, 1)",
      "@keyframes game-card-favorite-pop": {
        "0%": { transform: "scale(0.4)" },
        "100%": { transform: "scale(1)" },
      },
    },
  }),

  [theme.breakpoints.down("sm")]: {
    top: 6,
    right: 6,
    width: 30,
    height: 30,
    fontSize: 18,
    // A 44px target for a thumb, without a bigger star over the artwork.
    "&::after": { content: '""', position: "absolute", inset: -7 },
  },

  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover, &:active": { transform: "none" },
    "& svg": { animation: "none" },
  },
}));

/** The RTP chip in the card's other top corner, on the same dark glass as the star. */
// Stacks the RTP and bonus-buy chips in the card's corner.
export const CardChips = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 8,
  left: 8,
  zIndex: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 4,
  pointerEvents: "none",
  [theme.breakpoints.down("sm")]: { top: 6, left: 6, gap: 3 },
}));

export const RtpChip = styled(Typography)(({ theme }) => ({
  padding: "2px 7px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: "18px",
  color: colors.white,
  backgroundColor: alpha(FAVORITE_GLASS, 0.62),
  backdropFilter: "blur(6px)",
  border: `1px solid ${alpha("#ffffff", 0.28)}`,
  [theme.breakpoints.down("sm")]: { fontSize: 10, padding: "1px 6px" },
}));

export const TitleOverlayContent = styled(Box)(({ theme }) => ({
  color: gray[600],
  width: "100%",
  paddingBottom: 20,
  paddingLeft: 12,
  paddingRight: 12,
  ...theme.applyStyles("dark", {
    color: colors.white,
  }),
}));

export const GameNameText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  [theme.breakpoints.up("md")]: {
    fontSize: theme.typography.pxToRem(18),
  },
})) as typeof Typography;
